// dashboard/frontend/src/components/ConfigShadowSection.js
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDiscoveryRules, getRuleActivations, putRuleActivation,
  deleteRuleActivation, getShadowStats,
} from '../services/api';

// Copie du set canonique scripts/lib/rule_eval.py. Phase 1b : + 4 create-tx (parité 99%).
const LIVE_FEATURES = new Set([
  'snap_price', 'token_age_sec', 'progress',
  'real_liquidity_sol', 'virtual_liquidity_sol', 'unique_wallets_30s',
  'sol_buy_in_create_tx', 'pct_supply_create_tx',
  'n_atomic_buyers_create_tx', 'n_buyers_noncreator_create_tx',
]);
const evalOf = (f) => (LIVE_FEATURES.has(f) ? 'live' : 'batch');

const BATCH_WARN =
  'Couverture rétrospective (look-ahead). La feature (slot-0/bundle/holders) n’est PAS connue ' +
  'à l’instant de la décision live — reconstruite par cron offline a posteriori. La couverture ' +
  'batch n’est PAS une preuve d’exécutabilité : à valider en #4. Ne pas confondre ' +
  '"bonne couverture batch" avec "prête pour le live".';
const LIVE_NOTE =
  'Feature disponible à l’instant de la décision → un would_buy live prouve l’exécutabilité.';

function predKey(feature, op, threshold, slotTag) {
  return `${feature}|${op}|${threshold}|${slotTag || ''}`;
}

export default function ConfigShadowSection({ runId }) {
  const qc = useQueryClient();
  const [err, setErr] = useState(null);

  const rulesQ = useQuery({
    queryKey: ['disc-rules', runId, 'lift_oos'],
    queryFn: () => getDiscoveryRules({ runId, sort: 'lift_oos', order: 'desc' }),
  });
  const actQ = useQuery({ queryKey: ['disc-activations'], queryFn: getRuleActivations });
  const statsQ = useQuery({
    queryKey: ['disc-shadow-stats'], queryFn: () => getShadowStats({ windowHours: 24 }),
    refetchInterval: 10000,
  });

  const activations = actQ.data?.activations || [];
  const rules = rulesQ.data?.rules || [];

  // Index activations par prédicat pour savoir si une règle du run est activée.
  const actByPred = useMemo(() => {
    const m = new Map();
    activations.forEach((a) => m.set(predKey(a.feature, a.op, a.threshold, a.slot_tag), a));
    return m;
  }, [activations]);

  const statByAct = useMemo(() => {
    const m = new Map();
    (statsQ.data?.stats || []).forEach((s) => m.set(s.activation_id, s));
    return m;
  }, [statsQ.data]);

  const mutate = useMutation({
    mutationFn: (body) => putRuleActivation(body),
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ['disc-activations'] }); },
    onError: (e) => setErr(e?.response?.data?.detail || e.message),
  });
  const del = useMutation({
    mutationFn: (id) => deleteRuleActivation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['disc-activations'] }),
  });

  const activate = (r) => mutate.mutate({
    feature: r.feature, op: r.op, threshold: r.threshold, slot_tag: r.slot_tag,
    label_config: r.label_config, source_run_id: r.run_id, active: true,
  });

  // Activations en drift : actives mais absentes du run courant.
  const runPreds = useMemo(
    () => new Set(rules.map((r) => predKey(r.feature, r.op, r.threshold, r.slot_tag))), [rules]);

  const renderBadge = (ev) => (
    <span className="cs-badge" data-ev={ev} title={ev === 'batch' ? BATCH_WARN : LIVE_NOTE}>
      {ev}
    </span>
  );

  return (
    <div className="cs-wrap">
      <div className="cs-caveat">
        would_buy = observation shadow, n&apos;achète RIEN · lift OOS = signal relatif ·
        une règle <b>batch</b> : couverture rétrospective, exécutabilité live à valider en #4.
      </div>
      {err && <div className="cs-err">Erreur : {err}</div>}

      <div className="h1">Règles activables (dernier run)</div>
      <table className="disc-tbl">
        <thead><tr>
          <th>Prédicat</th><th>Éval.</th><th>Lift OOS</th><th>Activée</th>
          <th>size_mult</th><th>cap SOL</th><th>Action</th>
        </tr></thead>
        <tbody>
          {rules.map((r) => {
            const a = actByPred.get(predKey(r.feature, r.op, r.threshold, r.slot_tag));
            const ev = evalOf(r.feature);
            const st = a ? statByAct.get(a.id) : null;
            return (
              <tr key={r.id}>
                <td>{r.feature} {r.op} {r.threshold} <span className="cs-slot">[{r.slot_tag || '—'}]</span></td>
                <td>{renderBadge(ev)}{ev === 'batch'
                  && <span className="cs-warn" title={BATCH_WARN}>⚠</span>}</td>
                <td>{r.lift_oos == null ? '—' : Number(r.lift_oos).toFixed(2)}</td>
                <td>{a && a.active ? <span className="cs-on">oui</span> : <span className="tdp-muted">non</span>}
                  {st && ev === 'live' && <span className="cs-cnt"> · {st.live_count} wb/24h</span>}
                  {st && ev === 'batch' && st.batch_coverage
                    && <span className="cs-cnt" title={BATCH_WARN}> · cov {st.batch_coverage.coverage_pct}% ⚠</span>}
                </td>
                <td>{a?.size_mult ?? '—'}</td>
                <td>{a?.size_cap_sol ?? '—'}</td>
                <td>
                  {a && a.active
                    ? <button className="tdp-btn" onClick={() => del.mutate(a.id)}>Désactiver</button>
                    : <button className="tdp-btn on" onClick={() => activate(r)}>Activer</button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="h1" style={{ marginTop: 18 }}>Activations</div>
      <table className="disc-tbl">
        <thead><tr><th>Prédicat</th><th>Éval.</th><th>Active</th><th>Drift</th><th>Shadow</th></tr></thead>
        <tbody>
          {activations.map((a) => {
            const drift = !runPreds.has(predKey(a.feature, a.op, a.threshold, a.slot_tag));
            const st = statByAct.get(a.id);
            return (
              <tr key={a.id}>
                <td>{a.feature} {a.op} {a.threshold} <span className="cs-slot">[{a.slot_tag || '—'}]</span></td>
                <td>{renderBadge(a.evaluability)}</td>
                <td>{a.active ? 'oui' : 'non'}</td>
                <td>{drift ? <span className="cs-drift" title="Activée mais absente du dernier run">drift</span> : '—'}</td>
                <td>
                  {st && a.evaluability === 'live' && `${st.live_count} would_buy / 24h`}
                  {st && a.evaluability === 'batch' && st.batch_coverage
                    && <span title={BATCH_WARN}>cov {st.batch_coverage.coverage_pct}% ({st.batch_coverage.n_matched}/{st.batch_coverage.n_window}) ⚠</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
